import { Settings } from "@redotech/redo-model/team";
import { MerchantCarrierFeeSettingsValue } from "./merchant-carrier-fee-settings";

type OutboundLabelsSettings = NonNullable<Settings["outboundLabels"]>;
export type OutboundLabelsFees = NonNullable<OutboundLabelsSettings["fees"]>;
export type WmsFees = NonNullable<
  NonNullable<OutboundLabelsSettings["wms"]>["fees"]
>;

/**
 * Build the `outboundLabels.fees` update from the admin form value while
 * preserving billing-managed fields the UI does not control.
 *
 * The billing engine stamps `lastBilledDate` on these fee objects as its
 * idempotency guard against double charging (see
 * `redo/fulfillment/service/src/outbound-labels/merchant-label-fees.ts`).
 * Rebuilding the fees object from scratch on every settings save wiped those
 * timestamps, making the billing engine treat the team as never billed and
 * charge it again. Spreading the existing values keeps the UI in control of
 * amounts/`turnedOnAt` without clobbering billing state.
 */
export function buildOutboundLabelsFeesUpdate(
  existing: OutboundLabelsFees | undefined,
  value: MerchantCarrierFeeSettingsValue,
  chargeCreditCardProcessingFee: boolean,
): OutboundLabelsFees {
  return {
    ...existing,
    merchantCarrierAccountLabelFee: {
      ...existing?.merchantCarrierAccountLabelFee,
      amount: value.merchantCarrierAccountLabelFee.amount,
      tiktokShippingAmount:
        value.merchantCarrierAccountLabelFee.tiktokShippingAmount,
    },
    monthlySubscriptionFee: {
      ...existing?.monthlySubscriptionFee,
      amount: value.monthlySubscriptionFee.amount,
      turnedOnAt: value.monthlySubscriptionFee.turnedOnAt,
    },
    chargeCreditCardProcessingFee,
  };
}

/**
 * Build the `outboundLabels.wms.fees` update from the admin form value,
 * preserving the billing engine's `lastBilledDate` for the same reason as
 * {@link buildOutboundLabelsFeesUpdate}.
 */
export function buildWmsFeesUpdate(
  existing: WmsFees | undefined,
  wmsSubscriptionFee: MerchantCarrierFeeSettingsValue["wmsSubscriptionFee"],
): WmsFees {
  return {
    ...existing,
    monthlySubscriptionFee: {
      ...existing?.monthlySubscriptionFee,
      amount: wmsSubscriptionFee.amount,
      turnedOnAt: wmsSubscriptionFee.turnedOnAt,
    },
  };
}
